import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, RefreshCw, Search, Filter, Inbox, CheckCircle2, XCircle, Clock,
  Phone, Mail, MapPin, Calendar, Car, Users, X, FileText
} from "lucide-react";
import { listApplications, updateApplication, adminStats } from "../lib/api";
import { toast } from "sonner";
import { BRAND } from "../lib/constants";

const STATUS_CFG = {
  pending: { label: "Pending", color: "bg-[#FFD700] text-black", icon: Clock },
  contacted: { label: "Contacted", color: "bg-[#007AFF] text-white", icon: Phone },
  approved: { label: "Approved", color: "bg-[#00C853] text-white", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-[#FF3B30] text-white", icon: XCircle },
};

const StatusPill = ({ status, size = "sm" }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 ${cfg.color} font-mono font-bold uppercase tracking-widest ${size === "lg" ? "text-xs px-3 py-1.5" : "text-[10px] px-2 py-1"}`}>
      <Icon size={size === "lg" ? 14 : 11} /> {cfg.label}
    </span>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className="bg-[#0A0A0A] border border-white/15 p-5" data-testid={`stat-card-${label.toLowerCase()}`}>
    <div className="gd-tag text-white/40 mb-2">{label}</div>
    <div className={`font-display font-black text-4xl tracking-tighter ${accent || "text-white"}`}>{value}</div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, contacted: 0 });
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([listApplications(filter), adminStats()]);
      setApps(a);
      setStats(s);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("gd_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load applications");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("gd_admin_token")) {
      navigate("/admin/login");
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const logout = () => {
    localStorage.removeItem("gd_admin_token");
    navigate("/admin/login");
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return apps;
    const q = search.toLowerCase();
    return apps.filter((a) =>
      a.full_name.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q) ||
      a.phone.includes(q) ||
      a.city.toLowerCase().includes(q)
    );
  }, [apps, search]);

  const openDetail = (a) => {
    setSelected(a);
    setNotes(a.admin_notes || "");
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    try {
      const updated = await updateApplication(selected.id, status, notes);
      setSelected(updated);
      toast.success(`Marked as ${status}`);
      reload();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
            <img src={BRAND.logo} alt="" className="h-8 w-auto bg-white p-0.5" />
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none">GigDrive<span className="text-[#E60012]">.</span></div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Operator Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={reload} className="p-2 border border-white/20 hover:border-white/60 gd-snappy" data-testid="refresh-btn" aria-label="Refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} className="font-mono text-xs uppercase tracking-widest text-white/60 hover:text-[#E60012] inline-flex items-center gap-2 px-3 py-2 border border-white/20" data-testid="logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-8 md:py-10">
        {/* Stats */}
        <div className="mb-8">
          <div className="gd-tag text-[#E60012] mb-3">▌ Operations Overview</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter uppercase mb-6">Applications</h1>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            <StatCard label="Total" value={stats.total} />
            <StatCard label="Pending" value={stats.pending} accent="text-[#FFD700]" />
            <StatCard label="Contacted" value={stats.contacted} accent="text-[#007AFF]" />
            <StatCard label="Approved" value={stats.approved} accent="text-[#00C853]" />
            <StatCard label="Rejected" value={stats.rejected} accent="text-[#FF3B30]" />
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-5">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone, city..."
              className="w-full bg-black border border-white/20 pl-10 pr-4 py-3 font-mono text-sm focus:outline-none focus:border-[#E60012]"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            <Filter size={16} className="text-white/40 shrink-0" />
            {["all", "pending", "contacted", "approved", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`shrink-0 px-3 py-2 border font-mono text-[11px] uppercase tracking-widest gd-snappy ${
                  filter === s ? "bg-[#E60012] border-[#E60012] text-white" : "bg-black border-white/20 text-white/70 hover:border-white"
                }`}
                data-testid={`filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="border border-white/15 overflow-hidden" data-testid="applications-table">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-white/5 border-b border-white/15">
                <tr className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                  <th className="px-4 py-3">Driver</th>
                  <th className="px-4 py-3 hidden md:table-cell">Contact</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Location</th>
                  <th className="px-4 py-3 hidden md:table-cell">Vehicle</th>
                  <th className="px-4 py-3 hidden md:table-cell">Submitted</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-16 text-center text-white/40">
                    <Inbox size={40} className="mx-auto mb-3 opacity-50" />
                    <div className="font-display font-bold text-lg">No applications yet</div>
                    <div className="text-sm">When drivers apply, they'll show up here.</div>
                  </td></tr>
                )}
                {filtered.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => openDetail(a)}
                    className="border-b border-white/10 hover:bg-white/5 cursor-pointer gd-snappy"
                    data-testid={`row-${a.id}`}
                  >
                    <td className="px-4 py-4">
                      <div className="font-display font-bold text-base">{a.full_name}</div>
                      <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{a.gig_platforms.slice(0, 3).join(" · ")}{a.gig_platforms.length > 3 && ` +${a.gig_platforms.length - 3}`}</div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell font-mono text-xs text-white/70">
                      <div>{a.email}</div>
                      <div className="text-white/50">{a.phone}</div>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell font-mono text-xs text-white/70">{a.city}, {a.state}</td>
                    <td className="px-4 py-4 hidden md:table-cell font-mono text-xs uppercase">{a.vehicle_preference} · {a.rental_duration}</td>
                    <td className="px-4 py-4 hidden md:table-cell font-mono text-xs text-white/50">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-4"><StatusPill status={a.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex" data-testid="detail-drawer">
          <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={() => setSelected(null)}></div>
          <div className="w-full max-w-xl bg-[#0A0A0A] border-l-2 border-[#E60012] overflow-y-auto">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 p-5 flex items-center justify-between">
              <div>
                <div className="gd-tag text-[#E60012] mb-1">▌ Application</div>
                <div className="font-mono text-[10px] text-white/40">ID: {selected.id.slice(0, 8)}...</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 border border-white/20 hover:border-white" data-testid="close-drawer">
                <X size={16} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h2 className="font-display font-black text-3xl tracking-tighter mb-2">{selected.full_name}</h2>
                <StatusPill status={selected.status} size="lg" />
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info icon={Mail} label="Email" value={selected.email} />
                <Info icon={Phone} label="Phone" value={selected.phone} />
                <Info icon={MapPin} label="Location" value={`${selected.city}, ${selected.state}`} />
                <Info icon={Calendar} label="Applied" value={new Date(selected.created_at).toLocaleString()} />
              </div>

              <div className="border border-white/10 p-4">
                <div className="gd-tag text-[#E60012] mb-3">▌ License</div>
                <div className="font-mono text-sm">
                  Valid license: {" "}
                  {selected.has_valid_license === true ? (
                    <span className="text-[#00C853] font-bold">YES</span>
                  ) : selected.has_valid_license === false ? (
                    <span className="text-[#FF3B30] font-bold">NO</span>
                  ) : (
                    <span className="text-white/40">—</span>
                  )}
                </div>
              </div>

              <div className="border border-white/10 p-4">
                <div className="gd-tag text-[#E60012] mb-3">▌ Hustle</div>
                <div className="flex flex-wrap gap-2">
                  {selected.gig_platforms.map((p) => (
                    <span key={p} className="font-mono text-[11px] uppercase tracking-widest bg-white/5 border border-white/15 px-2 py-1">{p}</span>
                  ))}
                </div>
              </div>

              <div className="border border-white/10 p-4">
                <div className="gd-tag text-[#E60012] mb-3">▌ Vehicle Request</div>
                <div className="font-mono text-sm space-y-1">
                  <div className="inline-flex items-center gap-2"><Car size={14} /> {selected.vehicle_preference.toUpperCase()}</div>
                  <div className="inline-flex items-center gap-2"><Users size={14} /> {selected.rental_duration.toUpperCase()}</div>
                </div>
              </div>

              {selected.additional_info && (
                <div className="border border-white/10 p-4">
                  <div className="gd-tag text-[#E60012] mb-3">▌ Notes from driver</div>
                  <p className="text-sm text-white/80 leading-relaxed">{selected.additional_info}</p>
                </div>
              )}

              <div>
                <div className="gd-tag text-[#E60012] mb-2">▌ Internal notes</div>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add internal admin notes..."
                  className="w-full bg-black border border-white/20 px-3 py-3 font-mono text-sm focus:outline-none focus:border-[#E60012]"
                  data-testid="admin-notes-input"
                />
              </div>

              <div>
                <div className="gd-tag text-[#E60012] mb-3">▌ Update status</div>
                <div className="grid grid-cols-2 gap-2">
                  {["pending", "contacted", "approved", "rejected"].map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(s)}
                      className={`py-3 font-display font-bold uppercase text-xs tracking-widest border gd-snappy ${
                        selected.status === s
                          ? "bg-[#E60012] border-[#E60012] text-white"
                          : "bg-transparent border-white/20 text-white hover:border-[#E60012]"
                      }`}
                      data-testid={`status-${s}-btn`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="border border-white/10 p-3">
    <div className="gd-tag text-white/40 mb-1 inline-flex items-center gap-1.5"><Icon size={11} /> {label}</div>
    <div className="font-mono text-sm text-white break-words">{value}</div>
  </div>
);
